var Layout    = require("enyo-ui/Layout"),
    dom       = require("enyo/dom"),
    kind      = require("enyo/kind"),
    themeVars = require("app/theme/variables.js");

/**
 * This layout is meant to aid in positioning popups displayed in the
 * floatingLayer. Styles are applied to the container itself, which is expected
 * to be an [Popup]{@link app.ui.Popup} or
 * [ScrollerPopup]{@link app.ui.ScrollerPopup}.
 */
module.exports = kind(/** @lends  app.AppLayout.PopupLayout.prototype */ {

    /**
     * @private
     */
    name: "app.AppLayout.PopupLayout",

    /**
     * @private
     */
    kind: Layout,

    /**
     * Default options for the layout.
     *
     * @type {Object}
     * @protected
     */
    options: /** @lends  app.AppLayout.PopupLayout.prototype */ {

        /**
         * The popup will be aligned relative to a particular part of the app
         * layout.
         *
         * Current options include:
         * - `contentRegion`  Aligns the popup such that it's centered within
         *                    the bounds of the `contentRegion` of the app
         *                    layout. The height of both the popup and it's
         *                    scroller get limited to the height of the
         *                    `contentRegion` as well. This works well with
         *                    floating popups.
         * - `fullLayout`     Aligns the popup such that it's centered within
         *                    the bounds of the entire app layout. The height of
         *                    both the popup and it's scroller get limited to
         *                    the height of the entire app layout as well. This
         *                    works well with floating popups.
         * - `owner`          Aligns the popup such that it's centered within
         *                    the bounds of it's owner. The height of both the
         *                    popup and it's scroller get limited to the height
         *                    of it's owner as well. This works well with
         *                    non-floating popups.
         *
         * @type     {string|function}
         * @default  'contentRegion'
         * @public
         */
        alignment: "contentRegion",

        /**
         * If true, the popup expands to fill the region it's aligned with. If
         * false, it's contents determine it's size.
         *
         * @type     {boolean|function}
         * @default  false
         * @public
         */
        expand: false,

        /**
         * Whether the popup should be allowed to overlap the safe areas. This
         * only applies to `contentRegion` and `fullLayout` alignment.
         *
         * @type     {boolean|function}
         * @default  false
         * @public
         */
        overlapSafeArea: false,

        /**
         * Whether the popup should clear the floating app header. This only
         * applies to `contentRegion` alignment.
         *
         * @type     {boolean|function}
         * @default  false
         * @public
         */
        clearFloatingHeader: false,

        /**
         * Additional top margin around the popup.
         *
         * @type {number|function}
         * @default 0
         * @public
         */
        marginTop: 0,

        /**
         * Additional right margin around the popup.
         *
         * @type {number|function}
         * @default 0
         * @public
         */
        marginRight: 0,

        /**
         * Additional bottom margin around the popup.
         *
         * @type {number|function}
         * @default 0
         * @public
         */
        marginBottom: 0,

        /**
         * Additional left margin around the popup.
         *
         * @type {number|function}
         * @default 0
         * @public
         */
        marginLeft: 0,

        /**
         * The path from the popup to the client control.
         *
         * If `expand` is true, the client will be set to fit within
         * it's container.
         *
         * @type     {string|function|null}
         * @default  ".$.client"
         * @public
         */
        clientPath: ".$.client",

        /**
         * The path from the popup to the scroller.
         *
         * If `expand` is true, the scroller will be set to fit within it's
         * container. If `expand` is false, the scroller will receive a
         * max-height as calculated by the `calcScrollerMaxHeight()` method.
         *
         * @type     {string|function|null}
         * @default  ".$.scroller"
         * @public
         */
        scrollerPath: ".$.scroller",

        /**
         * The path from the popup to the scrollerDecorator.
         *
         * If `expand` is true, the scrollerDecorator will be set to fit within
         * it's container.
         *
         * @type     {string|function|null}
         * @default  ".$.scrollerDecorator"
         * @public
         */
        scrollerDecoratorPath: ".$.scrollerDecorator"
    },

    /**
     * @private
     */
    ownerBounds: null,

    /**
     * @private
     */
    popupAreaWidth: null,

    /**
     * @private
     */
    popupAreaHeight: null,

    /**
     * @private
     */
    constructor: kind.inherit(function(sup) {
        return function() {
            sup.apply(this, arguments);
            this.ownerBounds = {};
        };
    }),

    /**
     * @public
     */
    reflow: function() {
        var container         = this.container,
            appView           = container.app.view,
            scrollerDecorator = this.getScrollerDecorator(),
            scroller          = this.getScroller(),
            client            = this.getClient(),
            alignment         = this.getOption("alignment"),
            expand            = this.getOption("expand"),
            overlapSafeArea   = this.getOption("overlapSafeArea"),
            scrollerMaxHeight;

        if (!appView.hasNode() || !container.hasNode()) {
            return;
        }

        // Cache
        // --------------------------------

        if (alignment === "owner") {
            this.ownerBounds = container.owner.getAbsoluteBounds();
        }

        this.popupAreaWidth  = this.calcPopupAreaWidth();
        this.popupAreaHeight = this.calcPopupAreaHeight();

        // Css flags
        // --------------------------------

        expand ? this.setContainerClass("expanded") : this.unsetContainerClass("expanded");
        expand ? this.setScrimClass("expanded") : this.unsetScrimClass("expanded");

        alignment === "fullLayout"
            ? this.setContainerClass("aligns-with-full-layout")
            : this.unsetContainerClass("aligns-with-full-layout");

        alignment === "contentRegion"
            ? this.setContainerClass("aligns-with-content-region")
            : this.unsetContainerClass("aligns-with-content-region");

        if (overlapSafeArea && (
            alignment === "fullLayout" || alignment === "contentRegion"
        )) {
            this.setContainerClass("overlaps-safe-area");
        }
        else {
            this.unsetContainerClass("overlaps-safe-area");
        }

        // Centering
        // --------------------------------

        // Transform the popup such that it's visual center aligns with it's
        // top-left css coordinates.
        dom.transformValue(container, "translate", "-50%, -50%");

        // Center the popup within the layout area it's aligned with
        container.addStyles({
            top:  this.calcTopOffset() + "px",
            left: this.calcLeftOffset() + "px"
        });

        // Popup Size
        // --------------------------------

        // Expanded popup
        if (expand) {
            container.applyStyle("width", this.popupAreaWidth + "px");
            container.applyStyle("height", this.popupAreaHeight + "px");
            container.applyStyle("max-height", null);
        }

        // Popup size determined by content
        else {
            container.applyStyle("width", null);
            container.applyStyle("height", null);
            container.applyStyle("max-height", this.popupAreaHeight + "px");
        }

        // Scroller Size
        // --------------------------------

        // Expanded popup
        if (expand) {
            scrollerDecorator && scrollerDecorator.set("fit", true);
            scroller          && scroller.set("fit", true);
            client            && client.set("fit", true);
            scroller          && scroller.set("maxHeight", null);
        }

        // Popup size determined by content
        else {
            scrollerDecorator && scrollerDecorator.set("fit", false);
            scroller          && scroller.set("fit", false);
            client            && client.set("fit", false);
            scrollerMaxHeight = this.calcScrollerMaxHeight(this.popupAreaHeight);
            scroller          && scroller.set("maxHeight", scrollerMaxHeight + "px");
        }
    },

    /**
     * Update the scrim appropriately
     *
     * @private
     */
    onScrimCreated: function() {
        this.getOption("expand")
            ? this.setScrimClass("owner-is-expanded")
            : this.unsetScrimClass("owner-is-expanded");
    },

    /**
     * By default we use the `scrollerDecoratorPath` option to resolve the
     * scrollerDecorator. Override this method if another strategy would be
     * better.
     *
     * @returns  {enyo.Control}  The scrollerDecorator
     * @protected
     */
    getScrollerDecorator: function() {
        var scrollerDecoratorPath,
            scrollerDecorator;

        scrollerDecoratorPath = this.getOption("scrollerDecoratorPath") || null;

        if (scrollerDecoratorPath) {
            scrollerDecorator = this.container.get(scrollerDecoratorPath);
        }

        return scrollerDecorator || null;
    },

    /**
     * By default we use the `scrollerPath` option to resolve the scroller.
     * Override this method if another strategy would be better.
     *
     * @returns  {enyo.Control}  The scroller
     * @protected
     */
    getScroller: function() {
        var scrollerPath,
            scroller;

        scrollerPath = this.getOption("scrollerPath") || null;

        if (scrollerPath) {
            scroller = this.container.get(scrollerPath);
        }

        return scroller || null;
    },

    /**
     * By default we use the `clientPath` option to resolve the client.
     * Override this method if another strategy would be better.
     *
     * @returns  {enyo.Control}  The client
     * @protected
     */
    getClient: function() {
        var clientPath,
            client;

        clientPath = this.getOption("clientPath") || null;

        if (clientPath) {
            client = this.container.get(clientPath);
        }

        return client || null;
    },

    /**
     * Get the height of the headerRegion.
     *
     * @return {number} The height
     */
    getHeaderRegionHeight: function() {
        var appView            = this.container.app.view,
            headerRegionBounds = appView.get("headerRegionBounds") || {};

        return headerRegionBounds.height || 0;
    },

    /**
     * Get the height of the contentRegion.
     *
     * @return {number} The height
     */
    getContentRegionHeight: function() {
        var appView             = this.container.app.view,
            contentRegionBounds = appView.get("contentRegionBounds") || {};

        return contentRegionBounds.height || 0;
    },

    /**
     * Calculates the width of the area that can be spanned by the popup.
     *
     * @returns {number} The width
     */
    calcPopupAreaWidth: function() {
        var appView             = this.container.app.view,
            contentRegionBounds = appView.get("contentRegionBounds") || {},
            popupAreaWidth;

        switch (this.getOption("alignment")) {
            case "fullLayout":
            case "contentRegion":
                popupAreaWidth = contentRegionBounds.width || 0;

                if (!this.getOption("overlapSafeArea")) {
                    popupAreaWidth -= themeVars.safeAreaInsetLeft;
                    popupAreaWidth -= themeVars.safeAreaInsetRight;
                }
                break;
            case "owner":
                popupAreaWidth = this.ownerBounds.width || 0;
                break;
            default:
                popupAreaWidth = 0;
                break;
        }

        popupAreaWidth -= this.getOption("marginLeft");
        popupAreaWidth -= this.getOption("marginRight");

        return Math.max(popupAreaWidth, 0);
    },

    /**
     * Calculates the height of the area that can be spanned by the popup.
     *
     * @returns {number} The height
     */
    calcPopupAreaHeight: function() {
        var popupAreaHeight;

        switch (this.getOption("alignment")) {
            case "fullLayout":
                popupAreaHeight = this.getContentRegionHeight();

                if (themeVars.appHeaderFloat === false) {
                    popupAreaHeight += this.getHeaderRegionHeight();
                }

                if (!this.getOption("overlapSafeArea")) {
                    popupAreaHeight -= themeVars.safeAreaInsetTop;
                    popupAreaHeight -= themeVars.safeAreaInsetBottom;
                }

                break;
            case "contentRegion":
                popupAreaHeight = this.getContentRegionHeight();

                if (themeVars.appHeaderFloat === true
                    && this.getOption("clearFloatingHeader")) {
                    popupAreaHeight -= this.getHeaderRegionHeight();
                }

                if (!this.getOption("overlapSafeArea")) {
                    if (themeVars.appHeaderFloat === false
                        || !this.getOption("clearFloatingHeader")) {
                        popupAreaHeight -= themeVars.safeAreaInsetTop;
                    }
                    popupAreaHeight -= themeVars.safeAreaInsetBottom;
                }

                break;
            case "owner":
                popupAreaHeight = this.ownerBounds.height || 0;
                break;
            default:
                popupAreaHeight = 0;
                break;
        }

        popupAreaHeight -= this.getOption("marginTop");
        popupAreaHeight -= this.getOption("marginBottom");

        return Math.max(popupAreaHeight, 0);
    },

    /**
     * @returns  {number}  The top offset of the popup
     * @protected
     */
    calcTopOffset: function() {
        var offsetTop;

        switch (this.getOption("alignment")) {
            case "fullLayout":
                offsetTop = this.popupAreaHeight / 2;

                if (!this.getOption("overlapSafeArea")) {
                    offsetTop += themeVars.safeAreaInsetTop;
                }

                offsetTop += this.getOption("marginTop");

                return offsetTop;
            case "contentRegion":
                offsetTop = this.popupAreaHeight / 2;

                if (themeVars.appHeaderFloat === false
                    || (
                        themeVars.appHeaderFloat === true
                        && this.getOption("clearFloatingHeader")
                    )
                ) {
                    offsetTop += this.getHeaderRegionHeight();
                }
                else if (!this.getOption("overlapSafeArea")) {
                    offsetTop += themeVars.safeAreaInsetTop;
                }

                offsetTop += this.getOption("marginTop");

                return offsetTop;
            case "owner":
                return (this.ownerBounds.height || 0) / 2;
            default:
                return 0;
        }
    },

    /**
     * @returns  {number}  The left offset of the popup
     * @protected
     */
    calcLeftOffset: function() {
        var offsetLeft;

        switch (this.getOption("alignment")) {
            case "fullLayout":
            case "contentRegion":
                offsetLeft = this.popupAreaWidth / 2;

                if (!this.getOption("overlapSafeArea")) {
                    offsetLeft += themeVars.safeAreaInsetLeft;
                }

                offsetLeft += this.getOption("marginLeft");

                return offsetLeft;
            case "owner":
                return (this.ownerBounds.width || 0) / 2;
            default:
                return 0;
        }
    },

    /**
     * @param    {number}  popupMaxHeight The max height of the popup
     * @returns  {number}  The max height of the scroller
     * @private
     */
    calcScrollerMaxHeight: function(popupMaxHeight) {
        var computedStyles,
            borderTopWidth,
            borderBottomWidth;

        computedStyles    = window.getComputedStyle(this.container.hasNode(), null);
        borderTopWidth    = parseFloat(computedStyles.borderTopWidth, 10);
        borderBottomWidth = parseFloat(computedStyles.borderBottomWidth, 10);

        return popupMaxHeight - borderTopWidth - borderBottomWidth;
    },

    /**
     * Ensures the container has the supplied css class.
     *
     * @param  {string}  cssClass The css class
     * @private
     */
    setContainerClass: function(cssClass) {
        var container = this.container;

        !container.hasClass(cssClass) && container.addClass(cssClass);
    },

    /**
     * Ensures the container does not have the supplied css class.
     *
     * @param  {string}  cssClass The css class
     * @private
     */
    unsetContainerClass: function(cssClass) {
        var container = this.container;

        while (container.hasClass(cssClass)) {
            container.removeClass(cssClass);
        }
    },

    /**
     * Ensures the scrim has the supplied css class.
     *
     * @param  {string}  cssClass The css class
     * @private
     */
    setScrimClass: function(cssClass) {
        var scrim = this.container.scrim;

        if (!scrim) {
            return;
        }

        !scrim.hasClass(cssClass) && scrim.addClass(cssClass);
    },

    /**
     * Ensures the scrim does not have the supplied css class.
     *
     * @param  {string}  cssClass The css class
     * @private
     */
    unsetScrimClass: function(cssClass) {
        var scrim = this.container.scrim;

        if (!scrim) {
            return;
        }

        while (scrim.hasClass(cssClass)) {
            scrim.removeClass(cssClass);
        }
    }

});
